/**
 * Schritt 4 des Workload-Steckbriefs (NC-1279): fachliche Voraussetzungen.
 *
 * Die Lizenzfrage steht bewusst oben: cam ist an einer nie gestellten
 * Lizenzfrage gescheitert, aec an nie zugewiesenen Named-User-Konten.
 */

import React from 'react';
import { Alert, Box, Checkbox, FormControlLabel, MenuItem, TextField, Typography } from '@mui/material';
import { WorkloadProfileInput, ProfileOutputTarget, ProfileLanguage } from '../../../services/types';
import { ProfileStepProps } from './types';

export default function ProfileStepRequirements({ value, onChange }: ProfileStepProps) {
  const set = (patch: Partial<WorkloadProfileInput['requirements']>) =>
    onChange({ ...value, requirements: { ...value.requirements, ...patch } });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6">Fachliche Voraussetzungen</Typography>

      <TextField
        label="Lizenzsituation"
        helperText="Welcher Lizenztyp, und wer stellt vor der Schulung sicher, dass sie verfügbar sind?"
        multiline minRows={2} fullWidth
        value={value.requirements.licensing}
        onChange={(e) => set({ licensing: e.target.value })}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={value.requirements.hasInputData}
            onChange={(e) => set({ hasInputData: e.target.checked })}
          />
        }
        label="Teilnehmer bringen eigene Daten oder Projektdateien mit"
      />

      <TextField select label="Wohin geht das Arbeitsergebnis?" fullWidth
        value={value.requirements.outputTarget}
        onChange={(e) => set({ outputTarget: e.target.value as ProfileOutputTarget })}
      >
        <MenuItem value="none">Nichts wird zurückgegeben</MenuItem>
        <MenuItem value="collect-zip">Sammel-Download nach der Schulung</MenuItem>
        <MenuItem value="external-scm">In ein externes System, z.B. Quellcodeverwaltung</MenuItem>
      </TextField>

      {value.requirements.outputTarget === 'external-scm' && (
        <Alert severity="info">
          Die Plattform bietet derzeit nur den Sammel-Download. Ein Weg in ein
          externes System muss gesondert eingerichtet werden.
        </Alert>
      )}

      <TextField select label="Sprache der Oberfläche" fullWidth
        value={value.requirements.language}
        onChange={(e) => set({ language: e.target.value as ProfileLanguage })}
      >
        <MenuItem value="de-DE">Deutsch</MenuItem>
        <MenuItem value="en-US">Englisch</MenuItem>
      </TextField>

      <TextField
        label="Besonderheiten (optional)"
        helperText="Externe Zugänge, Datenschutz, Netzwerkfreigaben."
        multiline minRows={2} fullWidth
        value={value.requirements.notes}
        onChange={(e) => set({ notes: e.target.value })}
      />

      <TextField
        label="Abnahme-Kriterium"
        helperText="Woran erkennst du vor der Schulung, dass die Umgebung bereit ist?"
        multiline minRows={2} fullWidth
        value={value.acceptance.criterion}
        onChange={(e) => onChange({ ...value, acceptance: { criterion: e.target.value } })}
      />
    </Box>
  );
}
